import { useMemo, useEffect } from 'react';
import { useController, useWatch } from 'react-hook-form';
import Grid from '@mui/system/Unstable_Grid/Grid';
import { IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { isEmpty } from 'lodash';
import { useRootTranslation } from 'utils/hooks';
import { TLicensorType, TLicensorWithOneStop } from 'services/tracks/types';
import InputFormatField from 'components/common/Form/Fields/FormInputFormat/InputFormatField';
import { FormControlLabelWrapper } from 'components/common/Form/Fields/FormField/FormField';
import { extractArrayFieldError } from 'components/common/Form/Fields/FormField/extractArrayFieldError';
import { LICENSOR_TYPES, PUBLISHERS_TYPES_OPTIONS } from 'utils/constants';
import { RolePicker } from '../RolePicker';
import LicensorField from '../licensors/LicensorField';
import { PROPicker } from '../PROPicker';
import { RelatedSyncPublisherPicker } from './RelatedSyncPublisherPicker';
import { groupRightsOwners } from '../groupRightsOwners';
import { normalizeLicensorOutput } from '../normalizeLicensorOutput';
import { getLicensorValue } from '../getLicensorValue';

type PublisherRowFormProps = {
  index: number;
  htmlSectionId: string;
  removeField: () => void;
  type: TLicensorWithOneStop;
  oneStopCombined: boolean;
};

const useRelatedSyncPublisher = (oneStopCombined: boolean) => {
  const fields = useWatch({ name: 'rightsOwners' });

  return useMemo(() => {
    const rightOwnersMap = groupRightsOwners(fields);
    const syncFields = rightOwnersMap.get(LICENSOR_TYPES.SYNC) ?? [];
    const relatedSyncItems = oneStopCombined
      ? [
          ...syncFields,
          ...(rightOwnersMap.get(LICENSOR_TYPES.MASTER) ?? []),
          ...(rightOwnersMap.get(LICENSOR_TYPES.ONE_STOP) ?? []),
        ]
      : syncFields;
    return relatedSyncItems.filter(([it]) => !isEmpty(it.name)).map(([it]) => it);
  }, [fields, oneStopCombined]);
};

export function PublisherRowForm({ index, removeField, htmlSectionId, type, oneStopCombined }: PublisherRowFormProps) {
  const t = useRootTranslation();
  const { field, fieldState, formState } = useController({ name: `rightsOwners.${index}` });
  const nameError = extractArrayFieldError({
    fieldState,
    formState,
    arrayFieldName: 'rightsOwners',
    index,
    name: 'name',
  });

  const mappedRelatedSyncOptions = useRelatedSyncPublisher(oneStopCombined);

  // Auto-assign the first sync publisher if none is set
  useEffect(() => {
    if (!field.value?.relatedSyncPublisherId && mappedRelatedSyncOptions.length > 0) {
      const firstSyncId = mappedRelatedSyncOptions[0]?.id;
      if (firstSyncId) {
        field.onChange({ ...field.value, relatedSyncPublisherId: firstSyncId });
      }
    }
  }, [mappedRelatedSyncOptions, field.value?.relatedSyncPublisherId, field]);

  const licensorValue = getLicensorValue(field.value);

  const relatedSyncValue = field.value?.relatedSyncPublisherId
    ? mappedRelatedSyncOptions.find((it) => it.id === field.value?.relatedSyncPublisherId)
    : mappedRelatedSyncOptions?.at(0);

  return (
    <>
      <Grid xs={4}>
        <FormControlLabelWrapper name="name" error={Boolean(nameError)} helperText={t(nameError)}>
          <LicensorField
            types={[type] as TLicensorType[]}
            value={licensorValue}
            onChange={(nextValue) => {
              const result = normalizeLicensorOutput(field.value, nextValue);
              field.onChange(result);
            }}
            onCreate={(name: string) => {
              field.onChange({ ...field.value, name, qwireTracksRightsOwnerId: null, writerAffiliation: null });
            }}
            onEdit={(name) => {
              field.onChange({ ...field.value, name });
            }}
            htmlSectionId={htmlSectionId}
            hasError={Boolean(nameError)}
          />
        </FormControlLabelWrapper>
      </Grid>
      <Grid xs={4}>
        <RelatedSyncPublisherPicker
          value={relatedSyncValue}
          onChange={(nextValue) => {
            field.onChange({ ...field.value, relatedSyncPublisherId: nextValue.id });
          }}
          htmlSectionId={htmlSectionId}
          options={mappedRelatedSyncOptions}
        />
      </Grid>
      <Grid xs={2}>
        <RolePicker
          options={PUBLISHERS_TYPES_OPTIONS}
          labelledbyId={`${htmlSectionId}-role`}
          value={field.value?.role}
          onChange={(role) => {
            field.onChange({ ...field.value, role: role.id });
          }}
        />
      </Grid>
      <Grid xs={3}>
        <PROPicker
          htmlSectionId={htmlSectionId}
          value={field.value?.writerAffiliation ?? ''}
          onChange={(pro) => {
            field.onChange({ ...field.value, writerAffiliation: pro.id });
          }}
        />
      </Grid>
      <Grid xs={2}>
        <InputFormatField
          type="percentage"
          decimalScale={2}
          name="share"
          value={field.value?.share}
          onChange={(value) => {
            field.onChange({ ...field.value, share: value });
          }}
          fullWidth
          aria-labelledby={`${htmlSectionId}-share`}
        />
      </Grid>
      <Grid xs={1}>
        <IconButton onClick={removeField} aria-label={t('global.actions.removeButton')}>
          <DeleteIcon />
        </IconButton>
      </Grid>
    </>
  );
}
